import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, ChevronDown, LogOut, Search, UserRound } from "lucide-react";
import { CBadge, CButton, CCard, CCardBody, CFormInput } from "@coreui/react-pro";
import AppearanceToggle from "./AppearanceToggle";
import {
  applicantNavigationSections,
  flattenApplicantNavigationSections,
} from "../config/applicantNavigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { getStoredIdentity, logoutCurrentSession } from "../utils/authSession";

const brandLogoSrc = `${process.env.PUBLIC_URL}/assets/brand/experts4europe-logo.png`;

function getIdentity() {
  const { userid: storedUserid, firstname, lastname } = getStoredIdentity();
  const userid = storedUserid || "Bewerber";
  const displayName = [firstname, lastname].filter(Boolean).join(" ").trim() || userid;
  return { userid, displayName };
}

function ApplicantShell({
  title,
  subtitle,
  actions = null,
  notifications = [],
  searchItems = [],
  heroClassName = "",
  contentClassName = "",
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const identity = useMemo(() => getIdentity(), []);

  const flatNavigation = useMemo(
    () => flattenApplicantNavigationSections(applicantNavigationSections),
    []
  );
  const activeItem = flatNavigation.find((item) => item.match?.(location));
  const searchCatalog = useMemo(() => {
    const byKey = new Map();
    [...flatNavigation, ...(searchItems || [])].forEach((item) => {
      if (!item?.href || !item?.label) return;
      const key = `${item.href}:${item.label}`;
      if (!byKey.has(key)) byKey.set(key, item);
    });
    return Array.from(byKey.values());
  }, [flatNavigation, searchItems]);

  const filteredSearchItems = searchCatalog.filter((item) => {
    const haystack = `${item.label} ${item.description || ""}`.toLowerCase();
    return haystack.includes(searchQuery.trim().toLowerCase());
  });

  const visibleNotifications = notifications.length
    ? notifications
    : [{
        key: "dashboard",
        title: activeItem?.label || "Dashboard",
        text: "Status, nächster Schritt und Evidenz bleiben in derselben Applicant-Fläche sichtbar.",
        href: location.pathname + location.search,
      }];

  const goTo = (href) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(href);
  };

  const handleLogout = async () => {
    await logoutCurrentSession();
    window.location.assign("/login");
  };

  return (
    <div className="applicant-core-shell">
      <div className="applicant-core-grid">
        <aside className="applicant-core-sidebar">
          <CCard className="applicant-core-sidebar-card">
            <CCardBody className="applicant-core-sidebar-card__body">
              <div className="applicant-core-brand">
                <div className="applicant-core-brand__mark" aria-hidden="true">
                  <img src={brandLogoSrc} alt="" className="applicant-core-brand__logo" />
                </div>
                <div className="applicant-core-brand__copy">
                  <p className="applicant-core-brand__name">Experts4Europe</p>
                  <p className="applicant-core-brand__meta">Bewerberbereich</p>
                </div>
              </div>

              <div className="applicant-core-sidebar__intro">
                <div className="applicant-core-sidebar__intro-head">
                  <span className="applicant-core-kicker">Applicant OS</span>
                  <CBadge color="info" className="applicant-core-badge applicant-core-badge--muted">
                    {activeItem?.label || "Bereich"}
                  </CBadge>
                </div>
                <h2>{title || activeItem?.label || "Arbeitsbereich"}</h2>
                <p>{subtitle || "Dein Bereich fuehrt ueber naechste Aktion, klare Prioritaet und den passenden Applicant-Arbeitsraum."}</p>
              </div>

              <nav className="applicant-core-nav" aria-label="Navigation im Bewerberbereich">
                {applicantNavigationSections.map((section) => (
                  <div key={section.key || section.label} className="applicant-core-nav-group">
                    {section.label ? <span className="applicant-core-nav-group__label">{section.label}</span> : null}
                    <div className="applicant-core-nav-group__items">
                      {(section.items || []).map((item) => {
                        const isActive = item.match?.(location);
                        return (
                          <NavLink
                            key={item.href}
                            to={item.href}
                            className={`applicant-core-nav__item${isActive ? " is-active" : ""}`}
                          >
                            <span className="applicant-core-nav__icon">
                              <item.icon size={18} strokeWidth={2} />
                            </span>
                            <span>{item.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>

              <div className="applicant-core-sidebar__footer">
                <div className="applicant-core-sidebar__utility">
                  <span className="applicant-core-nav-group__label">Konto</span>
                  <p>Profil, Login und Rueckweg bleiben als Utility-Bereich getrennt von deinem Applicant-Arbeitsraum.</p>
                </div>
                <CButton
                  color="light"
                  variant="outline"
                  className="applicant-core-button applicant-core-button--secondary"
                  onClick={() => navigate("/dashboard/account")}
                >
                  <UserRound size={16} strokeWidth={2} />
                  <span>Konto</span>
                </CButton>
                <CButton
                  color="light"
                  variant="outline"
                  className="applicant-core-button applicant-core-button--secondary"
                  onClick={() => navigate("/")}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  <span>Zur Website</span>
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </aside>

        <main className="applicant-core-main">
          <header className="applicant-core-topbar">
            <div className="applicant-core-topbar__panel">
              <span className="applicant-core-kicker">Bewerberbereich</span>
              <strong>{activeItem?.label || "Dashboard"}</strong>
            </div>

            <div className="applicant-core-topbar__panel applicant-core-topbar__panel--center">
              <span className="applicant-core-kicker">Fokus</span>
              <strong>{title || activeItem?.label || "Bewerberbereich"}</strong>
            </div>

            <div className="applicant-core-topbar__actions">
              <CButton
                color="light"
                variant="ghost"
                className="applicant-core-icon-button"
                aria-label="Suche öffnen"
                onClick={() => setSearchOpen(true)}
              >
                <Search size={16} strokeWidth={2} />
              </CButton>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="applicant-core-icon-button" aria-label="Benachrichtigungen">
                    <Bell size={16} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="applicant-menu">
                  <DropdownMenuLabel>Hinweise</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {visibleNotifications.map((item) => (
                    <DropdownMenuItem key={item.key || item.href} onSelect={() => goTo(item.href)}>
                      <div className="applicant-menu__item">
                        <strong>{item.title}</strong>
                        <span>{item.text}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <AppearanceToggle compact />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="applicant-core-profile" aria-label="Profilmenü">
                    <div className="applicant-core-profile__avatar">
                      <UserRound size={16} strokeWidth={2} />
                    </div>
                    <div className="applicant-core-profile__copy">
                      <strong>{identity.displayName}</strong>
                      <span>{identity.userid}</span>
                    </div>
                    <ChevronDown size={14} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="applicant-menu">
                  <DropdownMenuLabel>Profilmenü</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => goTo("/dashboard/applicant")}>Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => goTo("/dashboard/applicant/edit-profile")}>Profil</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => goTo("/dashboard/account")}>Konto</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout}>
                    <span className="applicant-menu__logout">
                      <LogOut size={15} strokeWidth={2} />
                      <span>Abmelden</span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {(title || subtitle || actions) ? (
            <CCard className={["applicant-core-hero", heroClassName].filter(Boolean).join(" ")}>
              <CCardBody className="applicant-core-hero__body">
                <div className="applicant-core-hero__copy">
                  <div className="applicant-core-hero__meta">
                    <span className="applicant-core-kicker">Bewerber</span>
                    <CBadge color="info" className="applicant-core-badge">
                      {activeItem?.label || "Bereich"}
                    </CBadge>
                  </div>
                  {title ? <h1>{title}</h1> : null}
                  {subtitle ? <p>{subtitle}</p> : null}
                </div>
                {actions ? <div className="applicant-core-hero__actions">{actions}</div> : null}
              </CCardBody>
            </CCard>
          ) : null}

          <section className={["applicant-core-content", "applicant-shell__content", contentClassName].filter(Boolean).join(" ")}>
            {children}
          </section>
        </main>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="applicant-search-dialog applicant-core-search-dialog">
          <DialogHeader>
            <DialogTitle>Schnellzugriff</DialogTitle>
            <DialogDescription>
              Suche nach Bereichen, Aktionen und priorisierten Chancen im Bewerberbereich.
            </DialogDescription>
          </DialogHeader>
          <CFormInput
            type="search"
            className="applicant-search-dialog__input applicant-core-search-dialog__input"
            placeholder="Dashboard, Profil, Bewerbung, B1 ..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            autoFocus
          />
          <div className="applicant-search-dialog__results">
            {filteredSearchItems.length ? filteredSearchItems.map((item) => (
              <button
                key={`${item.href}-${item.label}`}
                type="button"
                className="applicant-search-dialog__item"
                onClick={() => goTo(item.href)}
              >
                <strong>{item.label}</strong>
                <span>{item.description || item.href}</span>
              </button>
            )) : (
              <div className="applicant-search-dialog__empty">
                Keine passende Aktion gefunden.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ApplicantShell;
